import {

  ChangeDetectionStrategy,

  Component,

  computed,

  input,

  model,

} from '@angular/core';

import { FormsModule } from '@angular/forms';

import { CurrencyPipe } from '@angular/common';

import { TranslocoDirective } from '@jsverse/transloco';

import { DonationLinkInput } from '../../../models/finance-models';

import { RosterParticipant } from '../../../models';



interface AssignmentRow {

  userId: number;

  name: string;

  selected: boolean;

  amount: number;

}



@Component({

  selector: 'app-participant-assignment-editor',

  imports: [FormsModule, CurrencyPipe, TranslocoDirective],

  templateUrl: './participant-assignment-editor.component.html',

  changeDetection: ChangeDetectionStrategy.OnPush,

})

export class ParticipantAssignmentEditorComponent {

  totalAmount = input.required<number>();

  assignEnabled = model.required<number>();

  links = model.required<DonationLinkInput[]>();

  roster = input.required<RosterParticipant[]>();



  rows = computed<AssignmentRow[]>(() => {

    const roster = this.roster();

    const linkMap = new Map(this.links().map((l) => [l.userId, l.amount]));



    return roster.map((p) => {

      const isSelected = linkMap.has(p.id);

      return {

        userId: p.id,

        name: `${p.firstname} ${p.lastname}`,

        selected: isSelected,

        amount: isSelected ? (linkMap.get(p.id) ?? 0) : 0,

      };

    });

  });



  assignedTotal = computed(() =>

    this.links().reduce((sum, link) => sum + link.amount, 0)

  );



  remaining = computed(() => this.totalAmount() - this.assignedTotal());



  isValid = computed(() => {

    if (!this.assignEnabled()) {

      return true;

    }

    return Math.abs(this.remaining()) < 0.01;

  });



  onAssignToggle(enabled: boolean): void {

    this.assignEnabled.set(enabled ? 1 : 0);

    if (!enabled) {

      this.links.set([]);

    }

  }



  toggleRow(row: AssignmentRow, selected: boolean): void {

    if (selected) {

      const exists = this.links().some((l) => l.userId === row.userId);

      if (!exists) {

        this.links.set([...this.links(), { userId: row.userId, amount: 0 }]);

      }

    } else {

      this.links.set(this.links().filter((l) => l.userId !== row.userId));

    }

  }



  updateAmount(row: AssignmentRow, amount: number | string | null): void {

    const parsed = amount === '' || amount === null ? 0 : Number(amount);

    const safeAmount = Number.isFinite(parsed) ? parsed : 0;



    const exists = this.links().some((l) => l.userId === row.userId);

    if (!exists) {

      if (!row.selected) {

        return;

      }

      this.links.set([...this.links(), { userId: row.userId, amount: safeAmount }]);

      return;

    }



    this.links.set(

      this.links().map((l) =>

        l.userId === row.userId ? { ...l, amount: safeAmount } : l

      )

    );

  }



  selectAll(selected: boolean): void {

    if (!selected) {

      this.links.set([]);

      return;

    }



    const existingAmounts = new Map(this.links().map((l) => [l.userId, l.amount]));

    this.links.set(

      this.roster().map((p) => ({

        userId: p.id,

        amount: existingAmounts.get(p.id) ?? 0,

      }))

    );

  }



  splitEqually(): void {

    const selectedLinks = this.links();

    if (selectedLinks.length === 0) {

      return;

    }



    const total = this.totalAmount();

    const perPerson = Math.floor((total / selectedLinks.length) * 100) / 100;

    let remainder = Math.round((total - perPerson * selectedLinks.length) * 100) / 100;



    this.links.set(

      selectedLinks.map((link) => {

        let amount = perPerson;

        if (remainder > 0) {

          amount = Math.round((amount + 0.01) * 100) / 100;

          remainder = Math.round((remainder - 0.01) * 100) / 100;

        }

        return { ...link, amount };

      })

    );

  }

}


